import { theme, Tooltip } from "antd";
import {
  CSSProperties,
  PropsWithChildren,
  useCallback,
  useState,
  useRef,
  useEffect,
  createContext,
  useContext,
  ReactNode,
  useMemo,
} from "react";
import { CaretLeftFilled, CaretRightFilled } from "@ant-design/icons";
import { Field, SearchActions } from "src/search/types";
import { SearchContext } from "src/search/useSearchContext";
import React from "react";
import { TableColumnHeaderTooltip } from "pages/[orgId]/explore/lib/tooltips/VZBLTooltips";
import debounce from "lodash/debounce";
import { TableFormatter } from "../utils/table-utils";

//TODO move this and use the new TableHeaderV2.  But need to
//integration the resize logic with the tables that use this header.
//we have inline styles that are likely competing with the global styles.
// this all needs to get cleaned up or retired.  Its the byproduct of
// the old table header that was replaced with the new one and multiple
// attempts to make the old one work and hacks. Blame Tim.

// Define the context type
interface TableHeaderContextType {
  renderTooltip?: (title: ReactNode, children: ReactNode) => ReactNode;
}

// Create the context
const TableHeaderContext = createContext<TableHeaderContextType>({});

// Create a provider component
export function TableHeaderProvider({
  children,
  renderTooltip,
}: PropsWithChildren<TableHeaderContextType>) {
  return (
    <TableHeaderContext.Provider value={{ renderTooltip }}>
      {children}
    </TableHeaderContext.Provider>
  );
}

// Hook to use the tooltip renderer
function useTableHeaderTooltip() {
  const context = useContext(TableHeaderContext);
  return context.renderTooltip;
}

export function TableHeaderCustomComponent(
  props: PropsWithChildren<{
    style: CSSProperties;
    className: string;
    resizable?: boolean;
    sortable?: boolean;
    movable?: boolean;
  }>
) {
  const renderTooltip = useTableHeaderTooltip();
  const {
    resizable = true,
    sortable = true,
    movable = true,
    ...restProps
  } = props;

  const title =
    typeof props.children === "string"
      ? props.children.replace(/[^\s\w]/g, "")
      : undefined;

  const [isResizing, setIsResizing] = useState(false);
  const resizeRef = useRef<{ startX: number; startWidth: number } | null>(null);
  const thRef = useRef<HTMLTableHeaderCellElement>(null);

  const startResize = (e: React.MouseEvent) => {
    if (!resizable || isResizing) return;
    setIsResizing(true);
    const currentWidth = thRef.current?.offsetWidth || 0;

    resizeRef.current = {
      startX: e.clientX,
      startWidth: currentWidth,
    };
  };

  const stopResize = () => {
    if (!isResizing) return;

    setIsResizing(false);
    resizeRef.current = null;
  };

  const debouncedResize = useMemo(
    () =>
      debounce((e: MouseEvent) => {
        if (!thRef.current || !resizeRef.current) return;

        const delta = e.clientX - resizeRef.current.startX;
        const width = Math.max(resizeRef.current.startWidth + delta, 0);

        const headerTable = thRef.current.closest("table");
        const colIndex = [
          ...(thRef.current.parentElement?.children || []),
        ].indexOf(thRef.current);
        const column = headerTable?.querySelectorAll("th")[colIndex]?.dataset;

        // Get minWidth from column properties or default to 50
        const minWidth = Number(column?.minWidth) || 50;
        const newWidth = `${Math.max(minWidth, width)}px`;

        // Find the data table - it should be in the same container
        const tableContainer = headerTable?.closest(".ant-table-container");
        const dataTable = tableContainer?.querySelector(
          ".ant-table-body table"
        ) as HTMLTableElement;

        // Update header cell width with explicit styles
        thRef.current.style.width = newWidth;
        thRef.current.style.minWidth = `${minWidth}px`;
        thRef.current.style.maxWidth = newWidth;
        thRef.current.style.boxSizing = "border-box";

        // Update all corresponding data cells in the data table
        const dataCells = dataTable?.querySelectorAll(
          `td:nth-child(${colIndex + 1})`
        );
        dataCells?.forEach((cell) => {
          (cell as HTMLElement).style.width = newWidth;
          (cell as HTMLElement).style.minWidth = `${minWidth}px`;
          (cell as HTMLElement).style.maxWidth = newWidth;
          (cell as HTMLElement).style.boxSizing = "border-box";
        });

        // Also update any other header cells in the same column
        const headerCells = headerTable?.querySelectorAll(
          `th:nth-child(${colIndex + 1})`
        );
        headerCells?.forEach((cell) => {
          if (cell !== thRef.current) {
            (cell as HTMLElement).style.width = newWidth;
            (cell as HTMLElement).style.minWidth = `${minWidth}px`;
            (cell as HTMLElement).style.maxWidth = newWidth;
            (cell as HTMLElement).style.boxSizing = "border-box";
          }
        });
      }, 1), // Increased to 32ms for better performance
    [] // Empty deps array since we use refs
  );

  const resize = useCallback(
    (e: MouseEvent) => {
      if (!isResizing) return;
      debouncedResize(e);
    },
    [isResizing, debouncedResize]
  );

  // Clean up the debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedResize.cancel();
    };
  }, [debouncedResize]);

  useEffect(() => {
    if (isResizing) {
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("mousemove", resize);
      window.addEventListener("mouseup", stopResize);
    }
    return () => {
      window.removeEventListener("mousemove", resize);
      window.removeEventListener("mouseup", stopResize);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [isResizing, resize, stopResize]);

  // Handle content rendering
  let content;
  if (Array.isArray(props.children)) {
    const [tooltipContent, textContent, ...sortCarets] = props.children;
    content = (
      <>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "4px",
            minWidth: 0,
            width: "100%",
          }}
        >
          {renderTooltip && tooltipContent ? (
            renderTooltip(
              tooltipContent,
              <div className="trunc" style={{ flex: 1 }}>
                {textContent}
              </div>
            )
          ) : (
            <div className="trunc" style={{ flex: 1 }}>
              {textContent || props.children}
            </div>
          )}
          {sortable && sortCarets && sortCarets.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
                paddingRight: "8px",
                justifyContent: "flex-end",
              }}
            >
              {sortCarets}
            </div>
          )}
        </div>
      </>
    );
  } else {
    content = (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "4px",
          minWidth: 0,
        }}
      >
        <div className="trunc">{props.children}</div>
      </div>
    );
  }

  return (
    <th
      {...restProps}
      ref={thRef}
      className={`${props.className} resizable-column`}
      style={{
        ...props.style,
        userSelect: isResizing ? "none" : "auto",
        width: thRef.current?.style.width || "auto",
        minWidth: thRef.current?.style.minWidth || "auto",
        maxWidth: thRef.current?.style.maxWidth || "none",
      }}
    >
      <style>{`
        .ant-table-container table {
          table-layout: fixed !important;
          width: 100% !important;
        }
      `}</style>
      <div title={title} className="table-header-content">
        {content}
      </div>
      {resizable && (
        <div
          className={`column-resizer ${isResizing ? "resizing" : ""}`}
          onMouseDown={startResize}
        />
      )}
    </th>
  );
}

export function GroupedTableHeader(props: {
  field: Field;
  index: number;
  params: SearchContext["params"];
  title?: string;
  showMovers?: boolean;
}) {
  const { token } = theme.useToken();

  const title = TableFormatter.formatDimension(
    props.title,
    props.field.attribute
  );

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
      {props.showMovers &&
        props.index > 0 &&
        (props.params.state.groupBys?.length || 0) > 1 && (
          <CaretLeftFilled
            title="Move Left"
            style={{
              color: token.colorTextQuaternary,
              fontSize: "12px",
              cursor: "pointer",
            }}
            onClick={() => {
              const bys = props.params.state.groupBys || [];
              props.params.dispatch({
                type: SearchActions.setGroupBys,
                value: [
                  ...bys.slice(0, props.index - 1),
                  bys.at(props.index)!,
                  bys.at(props.index - 1)!,
                  ...bys.slice(props.index + 1),
                ],
              });
            }}
          />
        )}
      <TableColumnHeaderTooltip field={props.field} title={title} />

      {props.showMovers &&
        props.index < (props.params.state.groupBys?.length || 0) - 1 &&
        (props.params.state.groupBys?.length || 0) > 1 && (
          <CaretRightFilled
            title="Move Right"
            style={{
              color: token.colorTextQuaternary,
              fontSize: "12px",
              cursor: "pointer",
            }}
            onClick={() => {
              const bys = props.params.state.groupBys || [];
              props.params.dispatch({
                type: SearchActions.setGroupBys,
                value: [
                  ...bys.slice(0, props.index),
                  bys.at(props.index + 1)!,
                  bys.at(props.index)!,
                  ...bys.slice(props.index + 2),
                ],
              });
            }}
          />
        )}
    </div>
  );
}
